import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, MonitorSmartphone, Loader2, LogOut } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN") : "—");

const deviceLabel = (ua = "") => {
  const b = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Browser";
  const o = /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Windows/.test(ua) ? "Windows" : /Mac OS/.test(ua) ? "macOS" : /Linux/.test(ua) ? "Linux" : "Unknown OS";
  return ua ? `${b} on ${o}` : "Unknown device";
};

export default function DevicesModal({ open, onClose }) {
  const { logoutAll } = useAuth();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { return; }
    setRows(null);
    api.get("/auth/sessions").then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [open]);

  if (!open) return null;

  const signOutAll = async () => {
    if (!window.confirm("Sign out from ALL devices, including this one?")) return;
    setBusy(true);
    try {
      await logoutAll();
      toast.success("Signed out from all devices");
      window.location.href = "/login";
    } catch {
      toast.error("Could not sign out all devices");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="devices-modal">
      <div className="bg-white rounded-lg w-full max-w-lg p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-black text-lg text-slate-900 flex items-center gap-2"><MonitorSmartphone className="w-5 h-5" /> Active devices</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" aria-label="Close" data-testid="devices-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded" data-testid="devices-list">
          {rows === null && <div className="p-4 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
          {rows?.map((r) => (
            <div key={r.id} className="p-3 text-xs" data-testid={`device-row-${r.id}`}>
              <div className="font-semibold text-slate-800 truncate" title={r.user_agent}>{deviceLabel(r.user_agent)}</div>
              <div className="text-slate-500 mt-0.5">IP {r.ip || "—"} · Last active {fmt(r.last_seen_at)} {r.current && <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold" data-testid="device-current">This device</span>}</div>
            </div>
          ))}
        </div>
        <button onClick={signOutAll} disabled={busy} className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold text-sm py-2.5 rounded disabled:opacity-50" data-testid="logout-all-devices-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />} Logout All Devices
        </button>
      </div>
    </div>
  );
}
